import { useState, useEffect } from 'react';

/**
 * Hook to detect when the mobile keyboard is open
 * Works by detecting significant viewport height changes
 */
export function useKeyboardVisibility(): boolean {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Store initial viewport height
    const initialHeight = window.visualViewport?.height || window.innerHeight;
    let lastHeight = initialHeight;

    // Threshold: if viewport shrinks by more than 150px, consider keyboard open
    const KEYBOARD_THRESHOLD = 150;

    const handleResize = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      
      // Check if height decreased significantly (keyboard opened)
      if (currentHeight < lastHeight - KEYBOARD_THRESHOLD) {
        setIsKeyboardVisible(true);
      }
      // Check if height returned close to initial (keyboard closed)
      else if (currentHeight >= initialHeight - 50) {
        setIsKeyboardVisible(false);
      }
      
      lastHeight = currentHeight;
    };

    // Use visualViewport API if available (better for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      // Fallback to window resize
      window.addEventListener('resize', handleResize);
    }

    // Also listen to focus/blur on input elements for better detection
    // Use event delegation to catch all inputs including dynamically added ones
    const handleFocus = (e: Event) => {
      const target = e.target as HTMLElement;
      // Only handle input, textarea, and contenteditable elements
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        // Small delay to let viewport adjust
        setTimeout(() => {
          const currentHeight = window.visualViewport?.height || window.innerHeight;
          if (currentHeight < initialHeight - KEYBOARD_THRESHOLD) {
            setIsKeyboardVisible(true);
          }
        }, 100);
      }
    };

    const handleBlur = (e: Event) => {
      const target = e.target as HTMLElement;
      // Only handle input, textarea, and contenteditable elements
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.contentEditable === 'true'
      ) {
        // Small delay to let viewport adjust
        setTimeout(() => {
          const currentHeight = window.visualViewport?.height || window.innerHeight;
          if (currentHeight >= initialHeight - 50) {
            setIsKeyboardVisible(false);
          }
        }, 100);
      }
    };

    // Use event delegation on document to catch all inputs
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  return isKeyboardVisible;
}

